import React, { useEffect, useRef, useState } from "react";
import { api, formatGHS } from "@/lib/api";
import {
  Car,
  ClipboardList,
  CheckCircle2,
  Clock,
  Loader2,
  DollarSign,
  Sparkles,
  TrendingUp,
  Bell,
  BellOff,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 30_000;
const LAST_SEEN_KEY = "titan_admin_last_seen_booking_count";

export default function AdminOverviewPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newCount, setNewCount] = useState(0); // bookings since last "mark as seen"
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const seenRef = useRef(parseInt(localStorage.getItem(LAST_SEEN_KEY) || "0", 10));

  const fetchStats = async (isPoll = false) => {
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
      // How many new bookings since the admin last opened this page?
      const total = data.total_bookings || 0;
      if (seenRef.current === 0) {
        // first ever load — treat current count as the baseline, don't alert
        seenRef.current = total;
        localStorage.setItem(LAST_SEEN_KEY, String(total));
      } else if (total > seenRef.current) {
        const diff = total - seenRef.current;
        setNewCount(diff);
        if (isPoll) {
          // Chime + toast + OS notification
          toast.success(`${diff} new booking${diff === 1 ? "" : "s"} just came in! Open Bookings to review.`, { duration: 10000 });
          try {
            // short ping sound (data-url: a tiny generated beep)
            const audio = new Audio("data:audio/wav;base64,UklGRlAAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YSwAAAAAAP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AP8A/wD/AA==");
            audio.volume = 0.6;
            audio.play().catch(() => {});
          } catch (_) { /* audio blocked - banner is enough */ }
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
              new Notification("New Titan booking", { body: `${diff} new booking${diff === 1 ? "" : "s"} — open the admin to review.`, tag: "titan-booking" });
            } catch (_) { /* notification API may be unavailable in some contexts */ }
          }
        }
      }
    } catch (e) {
      console.warn("admin stats poll failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(false);
    const id = window.setInterval(() => fetchStats(true), POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markSeen = () => {
    if (!stats) return;
    seenRef.current = stats.total_bookings || 0;
    localStorage.setItem(LAST_SEEN_KEY, String(seenRef.current));
    setNewCount(0);
    toast.info("Marked as seen.");
  };

  const requestNotifPermission = async () => {
    if (typeof Notification === "undefined") return toast.error("Browser notifications not supported here.");
    try {
      const perm = await Notification.requestPermission();
      setNotifPerm(perm);
      if (perm === "granted") toast.success("Browser notifications enabled.");
      else toast.info("Permission not granted.");
    } catch (e) {
      toast.error("Could not request permission.");
    }
  };

  if (loading) return <div className="p-10"><Loader2 className="animate-spin text-[#e63946]" /></div>;

  const CARDS = [
    { label: "Total cars", value: stats.total_cars, icon: Car },
    { label: "Available cars", value: stats.available_cars, icon: Car },
    { label: "Total bookings", value: stats.total_bookings, icon: ClipboardList },
    { label: "Confirmed", value: stats.confirmed_bookings, icon: CheckCircle2 },
    { label: "Pending", value: stats.pending_bookings, icon: Clock },
    { label: "New quote requests", value: stats.new_quotes || 0, icon: Sparkles },
    { label: "Marketing leads", value: stats.total_leads || 0, icon: TrendingUp },
    { label: "Revenue", value: formatGHS(stats.revenue), icon: DollarSign },
  ];

  return (
    <div className="p-8" data-testid="admin-overview-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <span className="eyebrow">Dashboard</span>
          <h1 className="font-display text-3xl font-bold text-[#0d2040] mt-2">Overview</h1>
        </div>
        <div className="flex items-center gap-2">
          {notifPerm !== "granted" && notifPerm !== "unsupported" && (
            <button
              onClick={requestNotifPermission}
              className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-bold border border-gray-300 hover:border-[#e63946] hover:text-[#e63946]"
              data-testid="admin-enable-notifications"
            >
              <BellOff size={14} /> Enable alerts
            </button>
          )}
          {notifPerm === "granted" && (
            <span className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-bold text-green-700" data-testid="admin-notifications-enabled">
              <Bell size={14} /> Alerts on
            </span>
          )}
        </div>
      </div>

      {newCount > 0 && (
        <div
          className="mt-6 bg-[#e63946] text-white p-4 flex items-center justify-between gap-3 shadow-lg"
          data-testid="admin-new-bookings-banner"
        >
          <div className="flex items-center gap-3">
            <Bell size={20} className="animate-pulse" />
            <div>
              <div className="font-bold uppercase tracking-widest text-sm">
                {newCount} new booking{newCount === 1 ? "" : "s"} received
              </div>
              <div className="text-xs text-white/80">Review and contact the customer to confirm payment.</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/admin/bookings"
              className="bg-white text-[#e63946] font-bold text-xs uppercase tracking-widest px-4 py-2 hover:bg-white/90"
              data-testid="admin-review-bookings"
            >
              Review now
            </Link>
            <button
              onClick={markSeen}
              className="text-xs uppercase tracking-widest text-white/80 hover:text-white"
              data-testid="admin-mark-seen"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
        {CARDS.map((c) => (
          <div key={c.label} className="bg-white border border-gray-200 p-6" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-gray-500">{c.label}</div>
              <div className="w-9 h-9 bg-[#0d2040] flex items-center justify-center">
                <c.icon size={16} className="text-white" />
              </div>
            </div>
            <div className="font-display text-3xl font-bold text-[#0d2040] mt-3">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 text-xs text-gray-400">
        Dashboard auto-refreshes every {POLL_INTERVAL_MS / 1000}s. Keep this tab open to receive new-booking alerts.
      </div>
    </div>
  );
}
